import { useLayoutEffect } from "react";

/**
 * Locks background page scrolling while a drawer / modal / bottom-sheet is open.
 *
 * antd's Drawer/Modal already set `overflow: hidden` on <body>, which is enough
 * on desktop — but iOS Safari ignores it and still lets the page behind scroll
 * ("scroll bleed"). This hook pins <body> with `position: fixed` (the reliable
 * iOS-safe technique) and restores the exact scroll position on close.
 *
 * It is reference-counted so nested/stacked drawers cooperate: only the first
 * lock pins the body and only the last release restores it.
 *
 * Usage:
 *   useScrollLock(showDrawer);           // single flag
 *   useScrollLock(isA || isB || isC);    // any-open flag
 */
let lockCount = 0;
let savedScrollY = 0;
let savedStyles: Partial<CSSStyleDeclaration> | null = null;

const applyLock = (): void => {
  savedScrollY = window.scrollY;
  const { style } = document.body;
  savedStyles = {
    position: style.position,
    top: style.top,
    left: style.left,
    right: style.right,
    width: style.width,
  };
  style.position = "fixed";
  style.top = `-${savedScrollY}px`;
  style.left = "0";
  style.right = "0";
  style.width = "100%";
};

const releaseLock = (): void => {
  if (!savedStyles) return;
  const { style } = document.body;
  style.position = savedStyles.position ?? "";
  style.top = savedStyles.top ?? "";
  style.left = savedStyles.left ?? "";
  style.right = savedStyles.right ?? "";
  style.width = savedStyles.width ?? "";
  savedStyles = null;
  window.scrollTo(0, savedScrollY);
};

const useScrollLock = (locked: boolean): void => {
  useLayoutEffect(() => {
    if (!locked) return;
    if (lockCount === 0) applyLock();
    lockCount += 1;
    return () => {
      lockCount -= 1;
      if (lockCount === 0) releaseLock();
    };
  }, [locked]);
};

export default useScrollLock;
